import React, { Component, Fragment } from 'react';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { Dropdown, OverlayTrigger, Popover } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import FormControl from 'react-bootstrap/FormControl';
import Overlay from 'react-bootstrap/Overlay';
import Tooltip from 'react-bootstrap/Tooltip';
import AdminLinkify from 'components/AdminLinkify';
import EmojiPicker from 'components/EmojiPicker';
import { CommentIcon, LuckIcon, PencilIcon, TrashIcon } from 'components/Icon';
import IconButton from 'components/IconButton';
import ProfileIcon from 'components/ProfileIcon';
import TextButton from 'components/TextButton';
import TheoData from 'components/TheoData';
import TimeSince from 'components/TimeSince';
import Youtubify from 'components/Youtubify';
import { SM_BREAKPOINT_AND_DOWN } from 'utils/constants';
import { fromIso9075DateString } from 'utils/dates';
import {
  cmDisplayName,
  getUserAdminLink,
  isCommunityManager
} from 'utils/roles';
import { userShape, wallCommentShape, wallPostShape } from 'utils/shapes';
import {
  canDeletePostOrComment,
  canEditPostOrComment,
  getUsersTooltipMessage
} from 'utils/wall';
import withMediaQuery from 'utils/withMediaQuery';
import './Comment.css';

class Comment extends Component {
  constructor(props) {
    super(props);
    this.commentAuthor = React.createRef();
    this.commentAvatar = React.createRef();
    this.textareaRef = React.createRef();

    this.state = {
      message: this.props.comment.comment,
      isDeleting: false,
      showAuthorTheo: false,
      showAvatarTheo: false,
      showEmojiPicker: false,
      activeCommentPopOverId: null
    };

    this.likeUnlikeComment = debounce(this.likeUnlikeComment, 500, {
      leading: true,
      trailing: false
    });

    this.handleChangeMessage = this.handleChangeMessage.bind(this);
    this.authorTheoClose = this.authorTheoClose.bind(this);
    this.avatarTheoClose = this.avatarTheoClose.bind(this);
  }

  componentDidMount() {
    document.body.addEventListener('click', this.handleOutsideClick);
    document.body.addEventListener('scroll', this.handleOutsideClick, true);
  }

  componentWillUnmount() {
    document.body.removeEventListener('click', this.handleOutsideClick);
    document.body.removeEventListener('scroll', this.handleOutsideClick, true);
  }

  handleOutsideClick = event => {
    const parent = document.getElementById('comment-dropdown');
    if (
      this.state.activeCommentPopOverId &&
      parent &&
      !parent.contains(event.target)
    ) {
      this.props.setActiveCommentSelected(null);
    }
  };

  componentDidUpdate(prevProps) {
    if (prevProps.comment.comment !== this.props.comment.comment) {
      this.setState({ message: this.props.comment.comment });
    }
    if (prevProps.activeCommentSelected !== this.props.activeCommentSelected) {
      this.setState({
        activeCommentPopOverId: this.props.activeCommentSelected
      });
    }
  }

  likeUnlikeComment(comment) {
    comment.likedByMe
      ? this.props.onUnlikeComment(comment)
      : this.props.onLikeComment(comment);
  }

  canEdit(comment) {
    return canEditPostOrComment(comment);
  }

  handleChangeMessage(event) {
    this.setState({ message: event.target.value });
  }

  handleCancelEdit(comment) {
    this.props.onCancelEditComment();
    this.setState({
      message: comment.comment
    });
  }

  handleEditClick(comment) {
    this.props.onEditComment(comment);

    requestAnimationFrame(() => {
      if (this.textareaRef.current) {
        const textarea = this.textareaRef.current;
        textarea.focus();
        const length = textarea.value.length;
        textarea.setSelectionRange(length, length);
      }
    });
  }

  canDelete(comment) {
    return canDeletePostOrComment(this.props.user, comment);
  }

  deleteComment(post, comment) {
    this.setState({ isDeleting: true }, () => {
      this.props.onDeleteComment(post.id, comment);
    });
  }

  authorTheoClose() {
    this.setState({
      showAuthorTheo: !this.state.showAuthorTheo
    });
  }

  avatarTheoClose() {
    this.setState({
      showAvatarTheo: !this.state.showAvatarTheo
    });
  }

  toggleEmojiPicker = () => {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  };

  addEmoji = emoji => {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text,
      showEmojiPicker: false
    });
  };

  renderEdit(comment) {
    const { onSaveEditComment } = this.props;
    const disabled = !this.state.message || !this.state.message.trim();
    const disabledSaveBtnClass = disabled ? 'disabled-save' : '';

    return (
      <div className="comment-edit-message">
        <FormControl
          as="textarea"
          value={this.state.message}
          onChange={this.handleChangeMessage}
          className="comment-message-text-area"
          rows="3"
          autoFocus
          ref={this.textareaRef}
        />
        <div className="edit-comment-btns">
          <EmojiPicker
            onEmojiPickerToggle={this.toggleEmojiPicker}
            showEmojiPicker={this.state.showEmojiPicker}
            addEmoji={this.addEmoji}
          />
          <Button
            className="cancel-edit-btn"
            variant="danger"
            onClick={() => this.handleCancelEdit(comment)}
          >
            Cancel
          </Button>
          <Button
            className={`save-edit-btn ${disabledSaveBtnClass}`}
            variant="primary"
            onClick={() => onSaveEditComment(this.state.message, comment)}
            disabled={disabled}
          >
            Save Changes
          </Button>
        </div>
      </div>
    );
  }

  handlePopoverToggle = popoverId => {
    this.props.setActiveCommentSelected(popoverId);
  };

  renderMessageBody(comment, authorLink, authorAlt, authorName) {
    const { post, showLegacyFeed } = this.props;
    let timestamp = comment.created;
    let prefix = '';
    if (comment.lastEditedAt) {
      timestamp = comment.lastEditedAt;
      prefix = 'edited ';
    }
    const { matchesBreakpoint: isMobileView } = this.props;
    const commentBodyClassName = showLegacyFeed
      ? 'legacy-feed-message-comment-body'
      : 'feed-message-comment-body';
    const commentTimeClassName = showLegacyFeed
      ? 'legacy-comment-time'
      : 'comment-time';

    let commentAvatarTheo;
    if (authorLink) {
      const authorAvatarButton = (
        <TextButton ref={this.commentAvatar} onClick={this.avatarTheoClose}>
          <ProfileIcon
            size={isMobileView ? 30 : 20}
            showLegacyFeed={showLegacyFeed}
            avatarData={comment.author.avatarData}
            legacyAvatarUrl={comment.author.avatarUrl}
            withBorder
            alt={authorAlt}
          />
        </TextButton>
      );
      commentAvatarTheo = (
        <Fragment>
          {authorAvatarButton}
          <Overlay
            rootClose={true}
            rootCloseEvent="Click"
            onHide={this.avatarTheoClose}
            target={this.commentAvatar.current}
            show={this.state.showAvatarTheo}
            placement="bottom"
          >
            <Tooltip
              className="comment-author-tooltip"
              id="comment-author-tooltip"
            >
              <TheoData
                link={authorLink}
                userId={comment.author.userId}
                onClick={this.avatarTheoClose}
              />
            </Tooltip>
          </Overlay>
        </Fragment>
      );
    }

    const actionsMenu = (
      <Popover id="comment-dropdown">
        <Popover.Body>
          <Dropdown>
            {this.canEdit(comment) && (
              <Dropdown.Item>
                <IconButton
                  icon={PencilIcon}
                  title
                  alt="Edit this comment"
                  label={'Edit'}
                  disabled={this.props.isEditingPostOrComment()}
                  onClick={() => {
                    this.handleEditClick(comment);
                  }}
                />
              </Dropdown.Item>
            )}
            {this.canDelete(comment) && (
              <Dropdown.Item>
                <IconButton
                  icon={TrashIcon}
                  size={'small'}
                  label={'Delete'}
                  title
                  alt="Delete this comment"
                  onClick={() => {
                    !this.state.isDeleting && this.deleteComment(post, comment);
                  }}
                  disabled={this.state.isDeleting}
                  confirmation="Are you sure you want to delete this comment?"
                />
              </Dropdown.Item>
            )}
          </Dropdown>
        </Popover.Body>
      </Popover>
    );

    return (
      <React.Fragment>
        {isMobileView ? (
          <div className="comment-specifics">
            <span
              className={`comment-body-container ${
                post && post.isPinned ? ' pinned' : ''
              }`}
            >
              <OverlayTrigger
                trigger="click"
                placement="bottom"
                overlay={actionsMenu}
                show={this.state.activeCommentPopOverId === comment.id}
                onToggle={() => this.handlePopoverToggle(comment.id)}
              >
                <span className="comment-actions-menu">•••</span>
              </OverlayTrigger>

              <span className="feed-message-header">
                {authorLink ? (
                  commentAvatarTheo
                ) : (
                  <ProfileIcon
                    size={30}
                    showLegacyFeed={showLegacyFeed}
                    avatarData={comment.author.avatarData}
                    legacyAvatarUrl={comment.author.avatarUrl}
                    className="user-avatar-icon"
                    withBorder
                    alt={authorAlt}
                  />
                )}
                <span className="comment-header">
                  <span className="feed-message-author-name notranslate">
                    {authorName}
                  </span>
                  <span className={commentTimeClassName}>
                    <TimeSince
                      date={fromIso9075DateString(timestamp)}
                      prefix={prefix}
                    />
                  </span>
                </span>
              </span>
              <span className="feed-message-content">
                <span className={commentBodyClassName}>
                  <Youtubify>
                    <AdminLinkify author={comment.author}>
                      {comment.comment}
                    </AdminLinkify>
                  </Youtubify>
                </span>
              </span>
              <span className="feed-message-footer">
                <IconButton
                  icon={LuckIcon}
                  size={'base'}
                  label={comment.likeCount === 0 ? '0' : comment.likeCount}
                  filled={comment.likedByMe}
                  title
                  alt="Luck this comment"
                  tooltipMessage={getUsersTooltipMessage(
                    comment.likedBy,
                    object => {
                      return object.displayName;
                    }
                  )}
                  tooltipClass="comment-like-icon-tooltip"
                />
              </span>
              <span className="feed-message-actions">
                <IconButton
                  icon={LuckIcon}
                  size={'base'}
                  count={comment.likeCount}
                  filled={comment.likedByMe}
                  label={'Luck'}
                  title
                  alt="Luck this comment"
                  tooltipMessage={getUsersTooltipMessage(
                    comment.likedBy,
                    object => {
                      return object.displayName;
                    }
                  )}
                  tooltipClass="comment-like-icon-tooltip"
                  onClick={() => this.likeUnlikeComment(comment)}
                />
                <IconButton
                  key="reply"
                  icon={CommentIcon}
                  size="base"
                  label={'Comment'}
                  tooltipMessage={'Comment'}
                  tooltipClass="post-comment-icon-tooltip"
                  onClick={() => this.props.openReply(post.id)}
                  alt="Comment"
                />
              </span>
            </span>
          </div>
        ) : (
          <div className="comment-specifics">
            <span
              className={`comment-body-container ${
                post && post.isPinned ? ' pinned' : ''
              }`}
            >
              {authorLink ? (
                commentAvatarTheo
              ) : (
                <ProfileIcon
                  size={30}
                  showLegacyFeed={showLegacyFeed}
                  avatarData={comment.author.avatarData}
                  legacyAvatarUrl={comment.author.avatarUrl}
                  className="user-avatar-icon"
                  withBorder
                  alt={authorAlt}
                />
              )}
              <span className="feed-message-content">
                <span className="feed-message-author-name notranslate">
                  {authorName}
                </span>
                <span className={commentBodyClassName}>
                  <Youtubify>
                    <AdminLinkify author={comment.author}>
                      {comment.comment}
                    </AdminLinkify>
                  </Youtubify>
                </span>
              </span>
            </span>
            <div className="comment-footer">
              <span className={commentTimeClassName}>
                <TimeSince
                  date={fromIso9075DateString(timestamp)}
                  prefix={prefix}
                />
              </span>
              <TextButton
                className="reply-link"
                onClick={() => this.props.openReply(post.id)}
              >
                Reply
              </TextButton>
              <IconButton
                icon={LuckIcon}
                size={'base'}
                count={comment.likeCount}
                filled={comment.likedByMe}
                title
                alt="Luck this comment"
                tooltipMessage={getUsersTooltipMessage(
                  comment.likedBy,
                  object => {
                    return object.displayName;
                  }
                )}
                tooltipClass="comment-like-icon-tooltip"
                onClick={() => this.likeUnlikeComment(comment)}
              />
              {this.canEdit(comment) && (
                <IconButton
                  icon={PencilIcon}
                  title
                  alt="Edit this comment"
                  disabled={this.props.isEditingPostOrComment()}
                  onClick={() => {
                    this.handleEditClick(comment);
                  }}
                />
              )}
              {this.canDelete(comment) && (
                <IconButton
                  icon={TrashIcon}
                  size={'small'}
                  title
                  alt="Delete this comment"
                  onClick={() => {
                    !this.state.isDeleting && this.deleteComment(post, comment);
                  }}
                  disabled={this.state.isDeleting}
                  confirmation="Are you sure you want to delete this comment?"
                />
              )}
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  render() {
    const { comment, user } = this.props;
    const isCm = isCommunityManager(user);
    const authorLink = isCm ? getUserAdminLink(comment.author) : null;
    const authorAlt = isCm ? `View ${comment.author.displayName} in admin` : '';
    let authorName = cmDisplayName(comment.author);
    if (authorLink) {
      authorName = (
        <Fragment>
          <TextButton ref={this.commentAuthor} onClick={this.authorTheoClose}>
            {authorName}
          </TextButton>
          <Overlay
            rootClose={true}
            rootCloseEvent="Click"
            onHide={this.authorTheoClose}
            target={this.commentAuthor.current}
            show={this.state.showAuthorTheo}
            placement="bottom"
          >
            <Tooltip
              className="comment-author-tooltip"
              id="comment-author-tooltip"
            >
              <TheoData
                link={authorLink}
                linkAlt={authorAlt}
                userId={comment.author.userId}
                onClick={this.authorTheoClose}
              />
            </Tooltip>
          </Overlay>
        </Fragment>
      );
    }

    return (
      <li className="comment-list-item">
        {this.props.editingComment === comment.id
          ? this.renderEdit(comment)
          : this.renderMessageBody(comment, authorLink, authorAlt, authorName)}
      </li>
    );
  }
}

Comment.propTypes = {
  comment: wallCommentShape,
  post: wallPostShape,
  onLikeComment: PropTypes.func,
  onUnlikeComment: PropTypes.func,
  user: userShape,
  isEditingPostOrComment: PropTypes.func,
  onEditComment: PropTypes.func,
  onCancelEditComment: PropTypes.func,
  onSaveEditComment: PropTypes.func,
  onDeleteComment: PropTypes.func,
  openReply: PropTypes.func,
  showLegacyFeed: PropTypes.bool
};

export default withMediaQuery(SM_BREAKPOINT_AND_DOWN)(Comment);
